import React, {
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  forwardRef,
} from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { css, Theme } from '@emotion/react';
import { getTrackingValues, mergeClassNames } from '../../core/utils/Utils';
import {
  animations,
  colors,
  shadows,
  spaces,
  text,
} from '../../core/styles/theme/default';
import IconSVG from '../../primitives/IconSVG/IconSVG';
import { IconInterface, LinkInterface, TrackingInterface } from '../../types';

const StyledButton = styled.button(
  ({ variant, outlined, theme }: ButtonProps & { theme: Theme }) => {
    const isSecondary = variant === 'secondary';

    const StyledButtonOutlined = css`
      background: ${colors.white};
      color: ${isSecondary ? colors.secondary : colors.primary};

      &:hover,
      &:focus {
        background: ${isSecondary
          ? colors.secondary300
          : 'linear-gradient(110deg, #253764 0%, #192543 100%);'};
        color: ${colors.white};
      }

      &:active {
        background: ${isSecondary ? colors.secondary400 : colors.primary400};
      }

      ${theme?.stylesExtension?.button?.outlined}
    `;

    const StyledButtonDefault = css`
      background: ${isSecondary
        ? colors.secondary
        : 'linear-gradient(110deg, #253764 0%, #192543 100%);'};
      color: ${colors.white};

      &:hover {
        box-shadow: ${shadows.ctaHover};
      }

      &:focus,
      &:active {
        background: ${isSecondary
          ? colors.secondary800
          : 'linear-gradient(110deg, #253764 0%, #192543 100%);'};
      }

      ${theme?.stylesExtension?.button?.default}
    `;

    return css`
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: ${rem(144)};
      padding: 0 ${rem(40)};
      border: ${rem(1)} solid;
      border-color: ${isSecondary ? colors.secondary : colors.primary};
      border-radius: ${rem(56)};
      text-decoration: none;
      cursor: pointer;
      transition: all ${animations.default};
      transition-property: background, color, border-color, box-shadow;
      height: ${rem(56)};
      ${text.p3};
      letter-spacing: 0.04rem;
      font-weight: 500;

      ${outlined ? StyledButtonOutlined : StyledButtonDefault}

      &:disabled {
        cursor: not-allowed;

        ${outlined
          ? css`
              border-color: ${colors.grey600};
              color: ${colors.grey600};
            `
          : css`
              background: ${colors.grey300};
              border-color: ${colors.grey300};
              color: ${colors.grey600};
            `}

        &:hover {
          background: inherit;
          box-shadow: none;
        }
      }
    `;
  },
);

const StyledButtonAsLink = styled.a(
  ({ variant, outlined, theme }: AnchorProps & { theme: Theme }) => {
    const isSecondary = variant === 'secondary';

    const StyledButtonOutlined = css`
      background: ${colors.white};
      color: ${isSecondary ? colors.secondary : colors.primary};

      &:hover,
      &:focus {
        background: ${isSecondary
          ? colors.secondary300
          : 'linear-gradient(110deg, #253764 0%, #192543 100%);'};
        color: ${colors.white};
      }

      &:active {
        background: ${isSecondary ? colors.secondary400 : colors.primary400};
      }

      ${theme?.stylesExtension?.button?.outlined}
    `;

    const StyledButtonDefault = css`
      background: ${isSecondary
        ? colors.secondary
        : 'linear-gradient(110deg, #253764 0%, #192543 100%);'};
      color: ${colors.white};

      &:hover {
        box-shadow: ${shadows.ctaHover};
      }

      &:focus,
      &:active {
        background: ${isSecondary
          ? colors.secondary800
          : 'linear-gradient(110deg, #253764 0%, #192543 100%);'};
      }

      ${theme?.stylesExtension?.button?.default}
    `;

    return css`
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: ${rem(144)};
      padding: 0 ${rem(40)};
      border: ${rem(1)} solid;
      border-color: ${isSecondary ? colors.secondary : colors.primary};
      border-radius: ${rem(56)};
      text-decoration: none;
      cursor: pointer;
      transition: all ${animations.default};
      transition-property: background, color, border-color, box-shadow;
      height: ${rem(56)};
      ${text.p3};
      letter-spacing: 0.04rem;
      font-weight: 500;

      ${outlined ? StyledButtonOutlined : StyledButtonDefault}

      &:disabled {
        cursor: not-allowed;

        ${outlined
          ? css`
              border-color: ${colors.grey600};
              color: ${colors.grey600};
            `
          : css`
              background: ${colors.grey300};
              border-color: ${colors.grey300};
              color: ${colors.grey600};
            `}

        &:hover {
          background: inherit;
          box-shadow: none;
        }
      }
    `;
  },
);

const StyledIconBefore = styled(IconSVG)`
  font-size: ${rem(20)};
  margin-right: ${rem(spaces.gamma)};
`;

const StyledIconAfter = styled(IconSVG)`
  font-size: ${rem(20)};
  margin-left: ${rem(spaces.gamma)};
`;

const ButtonContent = ({ buttonText, iconBefore, iconAfter }: ButtonProps) => {
  return (
    <React.Fragment>
      {iconBefore && <StyledIconBefore iconName={iconBefore} />}
      <span>{buttonText}</span>
      {iconAfter && <StyledIconAfter iconName={iconAfter} />}
    </React.Fragment>
  );
};

const Button = forwardRef(
  (
    {
      buttonText,
      variant = 'primary',
      iconBefore,
      iconAfter,
      link,
      tracking,
      className,
      href,
      ...rest
    }: ButtonProps | AnchorProps,
    ref,
  ) => {
    const linkProps = link
      ? {
          ...link,
          as: 'a',
        }
      : null;
    const trackingProps = getTrackingValues({ tracking });
    const classes = mergeClassNames({ className, tracking });
    const contentProps = {
      buttonText,
      iconBefore,
      iconAfter,
    };

    if (href) {
      const anchorProps = {
        variant,
        ref,
        ...classes,
        ...trackingProps,
        ...rest,
        href,
      };

      return (
        <StyledButtonAsLink {...(anchorProps as AnchorProps)}>
          <ButtonContent {...contentProps} />
        </StyledButtonAsLink>
      );
    }

    const buttonProps = {
      variant,
      ref,
      ...classes,
      ...linkProps,
      ...trackingProps,
      ...rest,
    };

    return (
      <StyledButton {...(buttonProps as ButtonProps)}>
        <ButtonContent {...contentProps} />
      </StyledButton>
    );
  },
);

Button.displayName = 'Button';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  href?: string;
  /** Text of the Button */
  buttonText: string;
  /** Variant Name of the Button - possible values: **primary**, **secondary** */
  variant?: 'primary' | 'secondary';
  /** Change to outlined Button */
  outlined?: boolean;
  /** Icon Name which will be displayed before the ButtonText */
  iconBefore?: IconInterface;
  /** Icon Name which will be displayed after the ButtonText */
  iconAfter?: IconInterface;
  /** Link Informations of the Button */
  link?: LinkInterface;
  /** Tracking Informations of the Button */
  tracking?: TrackingInterface;
  /** Class of the Button - for internal usage */
  className?: string;
  ref?: any;
  /** Function that will get executed when clicking on the Button */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export type AnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  /** Text of the Button */
  buttonText: string;
  /** Variant Name of the Button - possible values: **primary**, **secondary** */
  variant?: 'primary' | 'secondary';
  /** Change to outlined Button */
  outlined?: boolean;
  /** Icon Name which will be displayed before the ButtonText */
  iconBefore?: IconInterface;
  /** Icon Name which will be displayed after the ButtonText */
  iconAfter?: IconInterface;
  /** Link Informations of the Button */
  link?: LinkInterface;
  /** Tracking Informations of the Button */
  tracking?: TrackingInterface;
  /** Class of the Button - for internal usage */
  className?: string;
  ref?: any;
  /** Function that will get executed when clicking on the Button */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export default Button;
